import React, {useState} from 'react';
import {
  Pressable,
  ScrollView,
  View,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from 'react-native';
import Svg, {Defs, RadialGradient, Stop, Rect, Path} from 'react-native-svg';
import CCText from '@/Atoms/CCText';
import CCTextInput from '@/Atoms/CCTextInput';
import {useCreateClub} from '@/Hooks/ClubsApi/useClubsMutations';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import {VARIANTS} from './variantOptions';
import {
  BADGES,
  BADGE_BG_COLORS,
  DEFAULT_BADGE_ID,
  DEFAULT_BADGE_BG,
} from './badgeOptions';
import ClubBadge from './components/ClubBadge';

// A background-color swatch: a beveled, radial-gradient rounded square that
// mirrors the design prototype's bgSwatchVM styling.
function Swatch({
  base,
  ring,
  selected,
}: {
  base: string;
  ring: string;
  selected: boolean;
}) {
  return (
    <Svg width={34} height={34} viewBox="0 0 34 34">
      <Defs>
        <RadialGradient
          id={`sw-${base.replace('#', '')}`}
          cx="50%"
          cy="30%"
          rx="70%"
          ry="70%">
          <Stop offset="0" stopColor={base} />
          <Stop offset="1" stopColor={ring} />
        </RadialGradient>
      </Defs>
      <Rect
        x="0"
        y="0"
        width="34"
        height="34"
        rx="10"
        fill={`url(#sw-${base.replace('#', '')})`}
      />
      {/* inner bottom shade bevel */}
      <Rect
        x="1"
        y="1"
        width="32"
        height="32"
        rx="9"
        fill="none"
        stroke="#000000"
        strokeOpacity="0.15"
        strokeWidth="1.5"
      />
      {selected ? (
        <Rect
          x="1.5"
          y="1.5"
          width="31"
          height="31"
          rx="9"
          fill="none"
          stroke="#FACF47"
          strokeWidth="3"
        />
      ) : null}
    </Svg>
  );
}

// The 6th "custom color" swatch: a conic rainbow tile. On web it wraps a native
// <input type="color">; the picked color becomes the badge background.
function CustomSwatch({
  selected,
  value,
  onPick,
}: {
  selected: boolean;
  value: string;
  onPick: (hex: string) => void;
}) {
  const rainbow =
    'conic-gradient(from 0deg,#f32a52,#E6A817,#1ECF52,#3DAB9E,#67B3E1,#937ADB,#DB7ACC,#f32a52)';
  return (
    <View
      style={{
        width: 34,
        height: 34,
        borderRadius: 10,
        overflow: 'hidden',
        // @ts-ignore web-only background
        backgroundImage: Platform.OS === 'web' ? rainbow : undefined,
        backgroundColor: Platform.OS === 'web' ? undefined : '#937ADB',
        borderWidth: selected ? 3 : 0,
        borderColor: '#FACF47',
      }}>
      {Platform.OS === 'web'
        ? React.createElement('input' as any, {
            type: 'color',
            value,
            onChange: (e: any) => onPick(e.target.value),
            style: {
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              cursor: 'pointer',
              border: 'none',
              padding: 0,
            },
          })
        : null}
    </View>
  );
}

// Human-readable copy for backend ClubError codes (bad_name is handled inline).
function errorMessage(code: string): string {
  switch (code) {
    case 'rate_limited':
      return "You've hit the club creation limit. Try again later.";
    case 'club_limit_reached':
      return "You've reached the maximum number of clubs you can own.";
    case 'club_full':
      return 'This club is full.';
    case 'min_rating':
      return "Your rating doesn't meet this club's minimum.";
    default:
      return "Couldn't create the club. Please try again.";
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: '#FAF6EB',
  },
  wideScrollContent: {
    alignItems: 'center',
    backgroundColor: '#FAF6EB',
    paddingHorizontal: 24,
    paddingVertical: 28,
    flexGrow: 1,
  },
  wideCard: {
    width: '100%',
    maxWidth: 560,
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 32,
    borderWidth: 1,
    borderColor: '#E0DDD9',
  },
  wideVariantGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -5,
  },
  wideVariantItem: {
    width: '50%',
    padding: 5,
    // stretch the card to fill the row so paired cards match height
    alignSelf: 'stretch',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  backButtonText: {
    color: '#4D3F37',
    fontWeight: '600',
    fontSize: 15,
  },
  title: {
    fontSize: 28,
    color: '#262322',
    marginBottom: 20,
  },
  fieldGroup: {
    marginTop: 20,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#262322',
    marginBottom: 6,
  },
  input: {
    borderWidth: 1,
    borderColor: '#4D3F37',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    color: '#262322',
  },
  minRatingHint: {
    fontSize: 11.5,
    color: '#888280',
    marginTop: 6,
  },
  officialRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 13,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: 'rgba(38,35,34,0.12)',
    backgroundColor: '#fff',
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: '#C9BFA8',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  checkboxChecked: {
    backgroundColor: '#3DAB9E',
    borderColor: '#3DAB9E',
  },
  officialTitle: {
    fontSize: 13.5,
    fontWeight: '600',
    color: '#262322',
    marginBottom: 1,
  },
  officialHint: {
    fontSize: 11.5,
    color: '#888280',
  },
  descriptionInput: {
    borderColor: '#D4CFCA',
    minHeight: 72,
    paddingHorizontal: 13,
    paddingVertical: 11,
    textAlignVertical: 'top',
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#262322',
    marginBottom: 10,
  },
  variantRow: {
    padding: 13,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: 'rgba(38,35,34,0.12)',
    backgroundColor: '#fff',
    marginBottom: 8,
  },
  variantRowWide: {
    // fill the stretched grid cell + top-align so paired cards match height
    flex: 1,
    height: '100%',
    marginBottom: 0,
    alignItems: 'flex-start',
  },
  variantRowSelected: {
    backgroundColor: '#F0FAF8',
    borderColor: '#3DAB9E',
  },
  radioCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#C9BFA8',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  radioCircleSelected: {
    borderColor: '#3DAB9E',
    borderWidth: 2,
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#3DAB9E',
  },
  variantContent: {
    flex: 1,
  },
  variantTitle: {
    fontSize: 13.5,
    fontWeight: '600',
    color: '#262322',
    marginBottom: 1,
  },
  variantDescription: {
    fontSize: 11.5,
    color: '#888280',
  },
  submitButton: {
    backgroundColor: '#FACF47',
    borderRadius: 32,
    paddingVertical: 15,
    paddingHorizontal: 20,
    alignItems: 'center',
    marginTop: 20,
    marginBottom: 20,
  },
  submitButtonText: {
    color: '#4D3F37',
    fontWeight: '600',
    fontSize: 16,
  },
  errorText: {
    color: '#CD2C39',
    fontSize: 12,
    fontWeight: '500',
    marginTop: 8,
  },
  nameErrorText: {
    color: '#CD2C39',
    fontSize: 12,
    fontWeight: '500',
  },
  badgeHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  pieceToggle: {
    flexDirection: 'row',
    backgroundColor: '#EDE6D2',
    borderRadius: 999,
    padding: 3,
    gap: 2,
    width: 160,
  },
  pieceToggleSeg: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 5,
    borderRadius: 999,
  },
  pieceToggleSegActive: {
    backgroundColor: '#fff',
  },
  pieceToggleText: {
    fontSize: 12.5,
    fontWeight: '600',
    color: '#888280',
  },
  pieceToggleTextActive: {
    color: '#262322',
  },
  badgeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -6,
  },
  badgeItem: {
    width: '16.66%',
    padding: 6,
    alignItems: 'center',
  },
  badgeName: {
    fontSize: 11,
    fontWeight: '600',
    color: '#888280',
    marginTop: 6,
    textAlign: 'center',
    lineHeight: 13,
  },
  badgeNameSelected: {
    color: '#4D3F37',
  },
  swatchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
});

export default function CreateClub({navigation}: {navigation: any}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [variantKey, setVariantKey] = useState<string>('open');
  const [badgeId, setBadgeId] = useState<string>(DEFAULT_BADGE_ID);
  const [badgeBg, setBadgeBg] = useState<string>(DEFAULT_BADGE_BG);
  const [customBg, setCustomBg] = useState<string>('#3DAB9E');
  const [usingCustomBg, setUsingCustomBg] = useState(false);
  const [pieceColor, setPieceColor] = useState<'white' | 'black'>('white');
  const [gridW, setGridW] = useState(0);
  const [hoveredBadge, setHoveredBadge] = useState<string | null>(null);
  const [minRating, setMinRating] = useState('');
  const [official, setOfficial] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isStaff = useLoggedInUserStore(
    (s: any) => !!s.userData?.is_staff,
  );
  const createClub = useCreateClub();
  const isLoading = createClub.isLoading;
  const {isMobileOrTab} = useMediaQuery();
  const isWide = !isMobileOrTab;

  const submit = async () => {
    const v = VARIANTS.find(x => x.key === variantKey)!;
    setError(null);
    try {
      const trimmedRating = minRating.trim();
      const res = await createClub.mutateAsync({
        name,
        description,
        badge_id: badgeId,
        background_color: badgeBg,
        visibility: v.visibility,
        join_policy: v.join_policy,
        searchable: v.searchable,
        min_rating: trimmedRating === '' ? null : Number(trimmedRating),
        // Only staff can create an official club; backend also enforces this.
        official: isStaff ? official : undefined,
      } as any);

      // Show the success screen; it auto-redirects to the Clubs list after a
      // few seconds. Use navigate (not replace — these are drawer screens and
      // drawer navigators have no .replace()).
      navigation.navigate('ClubCreated', {clubId: res.club.id});
    } catch (e: any) {
      // A navigation failure must never masquerade as a create failure — only
      // treat it as an error when the API actually returned an error payload.
      if (e?.response?.data?.error) {
        setError(e.response.data.error);
      } else {
        // Club was created but navigation threw; still go to the success screen.
        navigation.navigate('Clubs');
      }
    }
  };

  const variantList = (
    <View style={isWide ? styles.wideVariantGrid : undefined}>
      {VARIANTS.map(v => (
        <View key={v.key} style={isWide ? styles.wideVariantItem : undefined}>
          <Pressable
            onPress={() => setVariantKey(v.key)}
            style={[
              styles.variantRow,
              isWide && styles.variantRowWide,
              variantKey === v.key && styles.variantRowSelected,
            ]}>
            <View
              style={[
                styles.radioCircle,
                variantKey === v.key && styles.radioCircleSelected,
              ]}>
              {variantKey === v.key && <View style={styles.radioInner} />}
            </View>
            <View style={styles.variantContent}>
              <CCText style={styles.variantTitle}>{v.label}</CCText>
              <CCText style={styles.variantDescription}>{v.description}</CCText>
            </View>
          </Pressable>
        </View>
      ))}
    </View>
  );

  const formBody = (
    <>
      <View>
        <CCText style={styles.fieldLabel}>Club name</CCText>
        <CCTextInput
          testID="create-name"
          placeholder="e.g. Sunday Tacticians"
          value={name}
          onChangeText={setName}
          style={styles.input}
        />
        {(error === 'bad_name' || error === 'name_taken') && (
          <CCText style={[styles.nameErrorText, {marginTop: 6}]}>
            {error === 'name_taken'
              ? '⚠ That name is already taken. Try another.'
              : "⚠ That name isn't available. Try another."}
          </CCText>
        )}
      </View>

      <View style={styles.fieldGroup}>
        <CCText style={styles.fieldLabel}>Description</CCText>
        <CCTextInput
          testID="create-desc"
          placeholder="What's this club about?"
          value={description}
          onChangeText={setDescription}
          style={[styles.input, styles.descriptionInput]}
          multiline
          numberOfLines={2}
        />
      </View>

      <View style={styles.fieldGroup}>
        <View style={styles.badgeHeaderRow}>
          <CCText style={styles.fieldLabel}>Pick a badge</CCText>
          <View style={styles.pieceToggle}>
            {(['white', 'black'] as const).map(c => (
              <Pressable
                key={c}
                onPress={() => setPieceColor(c)}
                style={
                  pieceColor === c
                    ? [styles.pieceToggleSeg, styles.pieceToggleSegActive]
                    : styles.pieceToggleSeg
                }>
                <CCText
                  style={
                    pieceColor === c
                      ? [styles.pieceToggleText, styles.pieceToggleTextActive]
                      : styles.pieceToggleText
                  }>
                  {c === 'white' ? 'White' : 'Black'}
                </CCText>
              </Pressable>
            ))}
          </View>
        </View>
        <View
          style={styles.badgeGrid}
          onLayout={e => setGridW(e.nativeEvent.layout.width)}>
          {BADGES.map(b => {
            const selected = badgeId === b.id;
            const crest = gridW > 0 ? gridW / 6 - 12 : 48;
            return (
              <Pressable
                key={b.id}
                testID={`create-badge-${b.id}`}
                onPress={() => setBadgeId(b.id)}
                onHoverIn={() => setHoveredBadge(b.id)}
                onHoverOut={() => setHoveredBadge(cur => (cur === b.id ? null : cur))}
                style={styles.badgeItem}>
                <ClubBadge
                  piece={b.piece}
                  color={badgeBg}
                  pieceColor={pieceColor}
                  size={crest}
                  selected={selected}
                  hovered={hoveredBadge === b.id}
                  animated
                  showCheck
                />
                <CCText
                  style={
                    selected
                      ? [styles.badgeName, styles.badgeNameSelected]
                      : styles.badgeName
                  }>
                  {b.name}
                </CCText>
              </Pressable>
            );
          })}
        </View>

        <View style={{marginTop: 12}}>
          <CCText style={[styles.fieldLabel, {marginBottom: 8}]}>
            Background color
          </CCText>
          <View style={styles.swatchRow}>
            {BADGE_BG_COLORS.map(c => (
              <Pressable
                key={c.value}
                testID={`create-bg-${c.value}`}
                onPress={() => {
                  setBadgeBg(c.value);
                  setUsingCustomBg(false);
                }}>
                <Swatch
                  base={c.value}
                  ring={c.ring}
                  selected={!usingCustomBg && badgeBg === c.value}
                />
              </Pressable>
            ))}
            <Pressable testID="create-bg-custom">
              <CustomSwatch
                selected={usingCustomBg}
                value={customBg}
                onPick={hex => {
                  setCustomBg(hex);
                  setBadgeBg(hex);
                  setUsingCustomBg(true);
                }}
              />
            </Pressable>
          </View>
        </View>
      </View>

      <View style={styles.fieldGroup}>
        <CCText style={styles.sectionLabel}>Who can join?</CCText>
        {variantList}
      </View>

      {/* Minimum rating (optional) */}
      <View style={styles.fieldGroup}>
        <CCText style={styles.fieldLabel}>Minimum rating (optional)</CCText>
        <CCTextInput
          testID="create-min-rating"
          placeholder="Leave blank for no minimum"
          value={minRating}
          onChangeText={t => setMinRating(t.replace(/[^0-9]/g, '').slice(0, 4))}
          keyboardType="number-pad"
          inputType="otp"
          style={styles.input}
        />
        <CCText style={styles.minRatingHint}>
          Players below this rating can't join. Leave empty to welcome everyone.
        </CCText>
      </View>

      {/* Official toggle — staff only (backend also enforces this). */}
      {isStaff ? (
        <View style={styles.fieldGroup}>
          <Pressable
            testID="create-official"
            onPress={() => setOfficial(o => !o)}
            style={styles.officialRow}>
            <View
              style={[
                styles.checkbox,
                official && styles.checkboxChecked,
              ]}>
              {official ? (
                <Svg width={13} height={13} viewBox="0 0 24 24">
                  <Path
                    d="M20 6 9 17l-5-5"
                    fill="none"
                    stroke="#fff"
                    strokeWidth={3}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </Svg>
              ) : null}
            </View>
            <View style={{flex: 1}}>
              <CCText style={styles.officialTitle}>Official club</CCText>
              <CCText style={styles.officialHint}>
                Adds the OFFICIAL badge. Official clubs don't compete in
                standings.
              </CCText>
            </View>
          </Pressable>
        </View>
      ) : null}

      {error && error !== 'bad_name' && error !== 'name_taken' && (
        <CCText style={styles.errorText}>{errorMessage(error)}</CCText>
      )}

      <Pressable
        testID="create-submit"
        onPress={submit}
        disabled={isLoading}
        style={styles.submitButton}>
        {isLoading ? (
          <ActivityIndicator color="#4D3F37" />
        ) : (
          <CCText style={styles.submitButtonText}>Create club</CCText>
        )}
      </Pressable>
    </>
  );

  if (isWide) {
    return (
      <ScrollView contentContainerStyle={styles.wideScrollContent}>
        <View style={{width: '100%', maxWidth: 560, marginBottom: 8}}>
          <Pressable
            onPress={() => navigation.goBack()}
            style={[styles.backButton, {marginBottom: 0}]}>
            <CCText style={styles.backButtonText}>‹ Back to clubs</CCText>
          </Pressable>
        </View>
        <View style={styles.wideCard}>
          <CCText fontWeight="thunder" style={styles.title}>Create a club</CCText>
          {formBody}
        </View>
      </ScrollView>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <Pressable onPress={() => navigation.goBack()} style={styles.backButton}>
        <CCText style={styles.backButtonText}>‹ Back</CCText>
      </Pressable>

      <CCText fontWeight="thunder" style={styles.title}>Create a club</CCText>
      {formBody}
    </ScrollView>
  );
}
